import { useMemo } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import PreferencesDataTable from '../../../../components/common/PreferencesDataTable';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { getTrainingDocumentFileName } from '../utils/documentFileName';

const VISIBILITY_TAGS = {
    public: { key: 'visibilityPublic', icon: 'pi pi-globe', severity: 'success' },
    private: { key: 'visibilityPrivate', icon: 'pi pi-lock', severity: 'warning' },
    mixed: { key: 'visibilityMixed', icon: 'pi pi-clone', severity: 'secondary' }
};

const TrainingDocumentTable = ({ documents, loading, onEdit, onPreview, readOnly = false, filters, onFilter }) => {
    const { th } = useLanguage();
    const columns = useMemo(
        () => [
            ...(!readOnly && onEdit
                ? [
                      {
                          field: '__actions',
                          header: '',
                          lockedFromPrefs: true,
                          lockedPosition: 'start',
                          style: { width: '4rem', textAlign: 'center' },
                          body: (row) => <Button type="button" icon="pi pi-pencil" text rounded aria-label={th('common.edit')} onClick={() => onEdit(row)} />
                      }
                  ]
                : []),
            { field: 'vehicleName', header: th('lmsTrainingDocument.vehicleModel'), body: (row) => row.vehicleName || '-', sortable: true, filter: true, filterPlaceholder: th('lmsTrainingDocument.vehicleModel') },
            { field: 'trainingName', header: th('lmsTrainingDocument.trainingType'), body: (row) => row.trainingName || '-', sortable: true, filter: true, filterPlaceholder: th('lmsTrainingDocument.trainingType') },
            { field: 'description', header: th('lmsTrainingDocument.description'), body: (row) => row.description || '-', sortable: true, filter: true, filterPlaceholder: th('lmsTrainingDocument.description') },
            {
                field: 'visibility',
                header: th('lmsTrainingDocument.visibility'),
                sortable: true,
                filter: true,
                showFilterMenu: false,
                style: { width: '12rem' },
                body: (row) => {
                    const tag = VISIBILITY_TAGS[row.visibility];
                    return tag ? <Tag value={th(`lmsTrainingDocument.${tag.key}`)} icon={tag.icon} severity={tag.severity} /> : '-';
                },
                filterElement: (options) => (
                    <Dropdown
                        value={options.value}
                        options={Object.entries(VISIBILITY_TAGS).map(([value, tag]) => ({ value, label: th(`lmsTrainingDocument.${tag.key}`) }))}
                        onChange={(e) => options.filterApplyCallback(e.value)}
                        placeholder={th('lmsTrainingDocument.filterVisibilityAll')}
                        className="p-column-filter w-full"
                        showClear
                    />
                )
            },
            ...(readOnly
                ? [
                      {
                          field: '__documents',
                          header: th('lmsTrainingDocument.libraryTitle'),
                          lockedFromPrefs: true,
                          body: (row) => (
                              <div className="flex flex-column align-items-start gap-1">
                                  {row.documents.map((document) => (
                                      <Button key={document.documentId} type="button" icon="pi pi-file-pdf" label={getTrainingDocumentFileName(document)} link className="p-0" onClick={() => onPreview(document)} />
                                  ))}
                              </div>
                          )
                      }
                  ]
                : [])
        ],
        [onEdit, onPreview, readOnly, th]
    );

    return (
        <PreferencesDataTable
            tableKey="lms-training-documents"
            columns={columns}
            value={documents}
            loading={loading}
            dataKey="vehicleTrainingMatchId"
            showGridlines
            filterDisplay="row"
            filters={filters}
            onFilter={onFilter}
            emptyMessage={th('lmsTrainingDocument.emptyMessage')}
        />
    );
};

export default TrainingDocumentTable;
